import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface SelectProps {
    title?: string;
    containerClassName?: string;
    className?: string;
    options?: { label: string; value: string }[];
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    required?: boolean;
    id?: string;
    disabled?: boolean;
}

export default function Select({
    title,
    containerClassName = '',
    className = '',
    options = [],
    value,
    onChange,
    placeholder = 'Select an option',
    required = false,
    id,
    disabled = false,
}: SelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState(value || '');
    const [position, setPosition] = useState<'top' | 'bottom'>('bottom');
    const [dropdownStyles, setDropdownStyles] = useState<{ top: number; left: number; width: number }>({
        top: 0,
        left: 0,
        width: 0,
    });
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const portalDropdownRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find((o) => o.value === selectedValue);

    useEffect(() => {
        if (value !== undefined) {
            setSelectedValue(value);
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const isClickInsideButton = buttonRef.current && buttonRef.current.contains(target);
            const isClickInsideDropdown = portalDropdownRef.current && portalDropdownRef.current.contains(target);

            if (!isClickInsideButton && !isClickInsideDropdown) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleSelect = (optionValue: string) => {
        setSelectedValue(optionValue);
        setIsOpen(false);
        if (onChange) {
            onChange(optionValue);
        }
    };

    const updateDropdownPosition = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const requiredSpace = 250;

            if (spaceBelow < requiredSpace && rect.top > requiredSpace) {
                setPosition('top');
                setDropdownStyles({
                    top: rect.top - 4,
                    left: rect.left,
                    width: rect.width,
                });
            } else {
                setPosition('bottom');
                setDropdownStyles({
                    top: rect.bottom + 4,
                    left: rect.left,
                    width: rect.width,
                });
            }
        }
    };

    const toggleDropdown = () => {
        if (disabled) return;

        if (!isOpen) {
            updateDropdownPosition();
        }
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        if (isOpen) {
            const handleScroll = () => {
                updateDropdownPosition();
            };
            const handleResize = () => {
                updateDropdownPosition();
            };

            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('scroll', handleScroll, true);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [isOpen]);

    return (
        <div className={containerClassName}>
            {title && (
                <label htmlFor={id} className="block mb-1 text-secondary font-semibold">
                    {title}
                    {required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
            )}
            <div className="relative" ref={dropdownRef}>
                <button
                    ref={buttonRef}
                    type="button"
                    id={id}
                    disabled={disabled}
                    className={`w-full appearance-none px-4 py-3 border border-[#D2D2D2] rounded-lg focus:ring-2 focus:ring-[#151515] focus:border-[#151515] outline-none transition-colors bg-gray-50 focus:bg-white text-[14px] text-left ${
                        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    } ${className}`}
                    onClick={toggleDropdown}
                >
                    <span className={selectedOption ? 'text-[#151515]' : 'text-[#52525B]'}>
                        {selectedOption ? selectedOption.label : placeholder}
                    </span>
                </button>
                <svg
                    className={`h-4 w-4 text-[#52525B] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none transition-transform ${
                        isOpen ? 'rotate-180' : ''
                    }`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                </svg>

                {/* Custom Dropdown - Rendered via Portal */}
                {isOpen &&
                    createPortal(
                        <div
                            ref={portalDropdownRef}
                            className="fixed z-[9999] bg-white border border-[#D2D2D2] rounded-lg shadow-lg max-h-60 overflow-auto"
                            style={
                                {
                                    top: position === 'top' ? 'auto' : `${dropdownStyles.top}px`,
                                    bottom:
                                        position === 'top' ? `${window.innerHeight - dropdownStyles.top}px` : 'auto',
                                    left: `${dropdownStyles.left}px`,
                                    width: `${dropdownStyles.width}px`,
                                } as React.CSSProperties
                            }
                        >
                            {options.map((option) => (
                                <div
                                    key={option.value}
                                    className={`px-4 py-3 cursor-pointer transition-colors text-[14px] hover:bg-gray-50 text-left ${
                                        selectedValue === option.value
                                            ? 'bg-gray-50 text-[#151515] font-semibold'
                                            : 'text-[#151515]'
                                    }`}
                                    onClick={() => handleSelect(option.value)}
                                >
                                    {option.label}
                                </div>
                            ))}
                            {options.length === 0 && (
                                <div className="px-4 py-3 text-[14px] text-[#52525B] text-center">
                                    No options available
                                </div>
                            )}
                        </div>,
                        document.body,
                    )}
            </div>
        </div>
    );
}
